<template>
  <div class="content">
    <Background>
      <FriendlierCard>
        <div class="padded-block">
          <div class="logo">
            <LocaleAwareLogo variant="primary" color="cloud" />
          </div>

          <Loading v-if="isValidating" isCenter isFull />

          <div v-else class="text-content">
            <!-- Invalid or expired token -->
            <div v-if="tokenValid === false" class="message invalid-token">
              <p class="is-cloud mt-3">{{ tokenErrorMessage }}</p>
              <router-link
                :to="{ path: '/', query: { show: 'forgot' } }"
                class="sunflower-button is-bold mt-4"
                aria-label="Request new password reset link"
              >
                Request New Link
              </router-link>
            </div>

            <!-- Valid token: show form or success -->
            <template v-else-if="tokenValid === true">
              <!-- Success state -->
              <div v-if="success" class="success-state">
                <div class="success-icon" aria-hidden="true">
                  <FontAwesomeIcon icon="circle-check" />
                </div>
                <p class="is-cloud mt-3">Password reset successfully! Redirecting to login...</p>
              </div>

              <!-- Reset form -->
              <div v-else>
                <h2 class="is-size-2 mb-4 mt-2 title">Reset Your Password</h2>

                <form @submit.prevent="handleSubmit" class="reset-form" novalidate>
                  <div v-if="showEmailField" class="friendlier-field">
                    <label for="reset-email" class="label is-cloud">Email</label>
                    <p class="control">
                      <span class="icon is-medium is-left mr-3">
                        <FontAwesomeIcon icon="envelope" />
                      </span>
                      <input
                        id="reset-email"
                        v-model="email"
                        name="email"
                        type="email"
                        autocomplete="email"
                        inputmode="email"
                        autocapitalize="off"
                        spellcheck="false"
                        placeholder="your@email.com"
                        class="is-size-4 is-right p-2"
                        :class="{ 'is-danger': emailError }"
                        :aria-invalid="!!emailError"
                        :aria-describedby="emailError ? 'email-error' : undefined"
                        @input="handleEmailInput"
                        @blur="validateEmail"
                      />
                    </p>
                    <p v-if="emailSuggestion" class="email-suggestion">
                      Did you mean
                      <button type="button" class="email-suggestion-link" @click="applyEmailSuggestion">{{ emailSuggestion }}</button>?
                    </p>
                    <p v-if="emailError" id="email-error" class="mt-2 has-text-danger error" role="alert">
                      {{ emailError }}
                    </p>
                  </div>

                  <div class="friendlier-field">
                    <label for="new-password" class="label is-cloud">New Password</label>
                    <p class="control">
                      <span class="icon is-medium is-left mr-3">
                        <FontAwesomeIcon icon="key" />
                      </span>
                      <input
                        id="new-password"
                        v-model="newPassword"
                        :type="showNewPassword ? 'text' : 'password'"
                        autocomplete="new-password"
                        placeholder="New password"
                        class="is-size-4 is-right p-2 password-input"
                        :class="{ 'is-danger': passwordError }"
                        :aria-invalid="!!passwordError"
                        :aria-describedby="passwordError ? 'password-error' : 'password-requirements'"
                        @input="onPasswordInput"
                        @blur="validatePassword"
                      />
                      <button
                        type="button"
                        class="password-toggle"
                        :aria-label="showNewPassword ? 'Hide password' : 'Show password'"
                        @click="showNewPassword = !showNewPassword"
                      >
                        <FontAwesomeIcon :icon="showNewPassword ? 'eye' : 'eye-slash'" />
                      </button>
                    </p>
                    <div id="password-requirements" class="requirements" aria-live="polite">
                      <p class="requirement" :class="{ met: newPassword.length >= 8 }">
                        <FontAwesomeIcon :icon="newPassword.length >= 8 ? 'circle-check' : 'circle'" />
                        Minimum 8 characters
                      </p>
                      <p class="requirement" :class="{ met: hasNumber }">
                        <FontAwesomeIcon :icon="hasNumber ? 'circle-check' : 'circle'" />
                        At least one number
                      </p>
                      <p class="requirement" :class="{ met: hasSpecial }">
                        <FontAwesomeIcon :icon="hasSpecial ? 'circle-check' : 'circle'" />
                        At least one special character
                      </p>
                    </div>
                    <p v-if="passwordStrength" class="strength" :class="passwordStrength">
                      Strength: {{ strengthLabel }}
                    </p>
                    <p v-if="passwordError" id="password-error" class="mt-2 has-text-danger error" role="alert">
                      {{ passwordError }}
                    </p>
                  </div>

                  <div class="friendlier-field">
                    <label for="confirm-password" class="label is-cloud">Confirm New Password</label>
                    <p class="control">
                      <span class="icon is-medium is-left mr-3">
                        <FontAwesomeIcon icon="key" />
                      </span>
                      <input
                        id="confirm-password"
                        v-model="confirmPassword"
                        :type="showConfirmPassword ? 'text' : 'password'"
                        autocomplete="new-password"
                        placeholder="Confirm new password"
                        class="is-size-4 is-right p-2 password-input"
                        :class="{ 'is-danger': confirmError }"
                        :aria-invalid="!!confirmError"
                        :aria-describedby="confirmError ? 'confirm-error' : undefined"
                        @input="validateConfirm"
                        @blur="validateConfirm"
                      />
                      <button
                        type="button"
                        class="password-toggle"
                        :aria-label="showConfirmPassword ? 'Hide password' : 'Show password'"
                        @click="showConfirmPassword = !showConfirmPassword"
                      >
                        <FontAwesomeIcon :icon="showConfirmPassword ? 'eye' : 'eye-slash'" />
                      </button>
                    </p>
                    <p v-if="confirmError" id="confirm-error" class="mt-2 has-text-danger error" role="alert">
                      {{ confirmError }}
                    </p>
                  </div>

                  <button
                    type="submit"
                    class="sunflower-button is-bold fs-1-2 mt-3"
                    :disabled="isFormInvalid || isSubmitting"
                    aria-busy="isSubmitting"
                  >
                    <span v-if="isSubmitting" class="spinner" aria-hidden="true"></span>
                    <span v-else>Reset Password</span>
                  </button>

                  <p v-if="submitError" class="mt-3 has-text-danger error" role="alert">
                    {{ submitError }}
                  </p>
                </form>
              </div>
            </template>

            <!-- No token: redirect or error -->
            <div v-else-if="tokenValid === null && !isValidating" class="message">
              <p class="is-cloud">No reset link provided. Redirecting to login...</p>
            </div>
          </div>
        </div>
      </FriendlierCard>
    </Background>
  </div>
</template>

<script>
import { onMounted, ref, computed, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

import { request } from "@/revamp/utils";
import { getQueryParams } from "@/revamp/utils";
import { emailValidator, getSuggestedEmail } from "@/revamp/utils";
import { passwordValidator, passwordMatch } from "@/revamp/utils";
import { Loading, FriendlierCard, Background, LocaleAwareLogo } from "@/revamp/components";
import { useFirebase } from "@/revamp/composables";

const MIN_LENGTH = 8;

const APIService = useFirebase();

export default {
  name: "ResetPassword",
  components: {
    Loading,
    FriendlierCard,
    Background,
    LocaleAwareLogo,
    FontAwesomeIcon,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();

    const token = ref("");
    const tokenValid = ref(null);
    const isValidating = ref(true);
    const tokenErrorMessage = ref("This reset link is invalid. Please request a new one.");
    const prefillEmail = ref("");

    const email = ref("");
    const emailSuggestion = ref(null);
    const emailError = ref("");
    const showEmailField = ref(true);

    const newPassword = ref("");
    const showNewPassword = ref(false);
    const passwordError = ref("");

    const confirmPassword = ref("");
    const showConfirmPassword = ref(false);
    const confirmError = ref("");

    const submitError = ref("");
    const isSubmitting = ref(false);
    const success = ref(false);

    const hasNumber = computed(() => /\d/.test(newPassword.value));
    const hasSpecial = computed(() => /[!@#$%^&*()\-_=+\[\]{}.,<>?~]/.test(newPassword.value));

    const passwordStrength = computed(() => {
      const p = newPassword.value;
      if (!p || p.length < MIN_LENGTH) return "";
      if (hasNumber.value && hasSpecial.value && p.length >= 10) return "strong";
      if (hasNumber.value || hasSpecial.value) return "medium";
      return "weak";
    });

    const strengthLabel = computed(() => {
      const s = passwordStrength.value;
      if (s === "strong") return "Strong";
      if (s === "medium") return "Medium";
      if (s === "weak") return "Weak";
      return "";
    });

    const isFormInvalid = computed(() => {
      if (!newPassword.value || !confirmPassword.value) return true;
      if (emailError.value || passwordError.value || confirmError.value) return true;
      if (showEmailField.value && !email.value.trim()) return true;
      const pErr = passwordValidator(newPassword.value, (k, d) => d);
      const cErr = passwordMatch(newPassword.value, confirmPassword.value, (k, d) => d);
      return !!pErr || !!cErr;
    });

    function validateEmail() {
      emailError.value = "";
      emailSuggestion.value = getSuggestedEmail(email.value);
      if (!email.value.trim()) {
        if (showEmailField.value) emailError.value = "Please enter your email.";
        return;
      }
      if (!emailValidator(email.value)) {
        emailError.value = "Please enter a valid email address.";
      }
    }

    function handleEmailInput() {
      emailSuggestion.value = null;
    }

    function applyEmailSuggestion() {
      if (!emailSuggestion.value) return;
      email.value = emailSuggestion.value;
      emailSuggestion.value = null;
      validateEmail();
    }

    function validatePassword() {
      const msg = passwordValidator(newPassword.value, (k, def) => def);
      passwordError.value = msg || "";
    }

    function validateConfirm() {
      const msg = passwordMatch(newPassword.value, confirmPassword.value, (k, def) => def);
      confirmError.value = msg || "";
    }

    function onPasswordInput() {
      passwordError.value = "";
      validateConfirm();
    }

    watch(confirmPassword, () => {
      confirmError.value = "";
      validateConfirm();
    });

    async function validateToken() {
      let tokenParam = route.query?.token;
      if (!tokenParam) {
        const params = await getQueryParams(false);
        tokenParam = params.token || params.user;
      }

      if (!tokenParam) {
        tokenValid.value = false;
        tokenErrorMessage.value = "This reset link is invalid. Please request a new one.";
        isValidating.value = false;
        return;
      }

      token.value = tokenParam;

      const checkResponse = await APIService.checkResetPasswordLink(tokenParam);
      if (checkResponse && checkResponse.isValid) {
        tokenValid.value = true;
        if (checkResponse.email) {
          prefillEmail.value = checkResponse.email;
          email.value = checkResponse.email;
          showEmailField.value = false;
        }
      } else {
        tokenValid.value = false;
        tokenErrorMessage.value =
          checkResponse?.expired ?? false
            ? "This reset link has expired. Please request a new one."
            : "This reset link is invalid. Please request a new one.";
      }
      isValidating.value = false;
    }

    async function handleSubmit() {
      submitError.value = "";
      validateEmail();
      validatePassword();
      validateConfirm();

      if (isFormInvalid.value) return;

      const emailToSend = email.value.trim().toLowerCase() || prefillEmail.value;
      if (!emailToSend) {
        submitError.value = "Please enter your email.";
        return;
      }

      isSubmitting.value = true;

      try {
        const response = await request(
          `${process.env.VUE_APP_FIREBASE_FUNCTIONS_URL}/resetPassword`,
          "POST",
          {
            email: emailToSend,
            newPassword: newPassword.value,
            token: token.value,
          },
          "ResetPassword.submit",
          false
        );

        if (response.status === "success" || response.success) {
          success.value = true;
          setTimeout(() => {
            router.replace({ path: "/", query: { reset: "success" } });
          }, 2000);
        } else {
          submitError.value = response.message || "Something went wrong. Please try again.";
        }
      } catch (err) {
        submitError.value = err.message || "Something went wrong. Please try again.";
      } finally {
        isSubmitting.value = false;
      }
    }

    onMounted(() => {
      validateToken();
    });

    return {
      tokenValid,
      isValidating,
      tokenErrorMessage,
      success,
      email,
      emailSuggestion,
      emailError,
      showEmailField,
      newPassword,
      showNewPassword,
      passwordError,
      confirmPassword,
      showConfirmPassword,
      confirmError,
      hasNumber,
      hasSpecial,
      passwordStrength,
      strengthLabel,
      isFormInvalid,
      isSubmitting,
      submitError,
      validateEmail,
      handleEmailInput,
      applyEmailSuggestion,
      validatePassword,
      validateConfirm,
      onPasswordInput,
      handleSubmit,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "@/revamp/assets/scss/colors.scss";
@import "@/revamp/assets/scss/elements.scss";

.padded-block {
  padding: 20px;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  text-align: center;
}

.logo {
  max-width: 30%;
  min-width: 200px;
  margin: 0 auto 1rem;
}

.title {
  color: $sunflower;
}

.message {
  p {
    color: $cloud;
    margin: 10px 0;
  }
  &.invalid-token {
    background-color: rgba($denim, 0.9);
    padding: 1.5rem;
    border-radius: 8px;
  }
}

.success-state {
  .success-icon {
    color: $green-highlight;
    font-size: 3rem;
  }
}

.reset-form {
  text-align: left;
}

.label {
  display: block;
  margin-bottom: 0.25rem;
  font-size: 0.95rem;
}

.email-suggestion {
  margin-top: 0.5rem;
  color: #4a4a4a;
}

.email-suggestion-link {
  appearance: none;
  border: none;
  background: none;
  color: #0048b4;
  font: inherit;
  padding: 0;
  text-decoration: underline;
  cursor: pointer;
}

.friendlier-field {
  margin-bottom: 1rem;

  .control {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    input {
      flex: 1;
      min-width: 0;
      width: 100%;
    }
  }

  input.password-input {
    padding-right: 3rem !important;
  }
}

.password-toggle {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: $sunflower;
  cursor: pointer;
  padding: 0.25rem;
  line-height: 1;
  &:focus {
    outline: 2px solid $ocean;
    outline-offset: 2px;
  }
}

.requirements {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: $cloud;

  .requirement {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0.25rem 0;

    &.met {
      color: $green-highlight;
    }
  }
}

.strength {
  margin-top: 0.5rem;
  font-size: 0.875rem;

  &.weak {
    color: $red-highlight;
  }
  &.medium {
    color: $sunflower;
  }
  &.strong {
    color: $green-highlight;
  }
}

.spinner {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error {
  font-size: 0.9rem;
}
</style>
